import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
import { RiErrorWarningLine } from 'react-icons/ri';
import { RxCross2 } from 'react-icons/rx';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Loader from '../../../components/Loader';
import { resetBankIDFlow, resetBankIDState } from '../../features/bankid/bankid.slice';
import { useDispatch, useSelector } from 'react-redux';

export interface ReactivateModalProps {
    show: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    email?: string; // Regular email (from form or userData)
    getParamsEmail?: string; // Email from URL parameters
    qrCodeEmail?: any; // Entire userData object from BankID flow (like old implementation)
    inactiveAccount?: boolean;
    mode?: 'login' | 'signup';
}

const ReactivateModal: React.FC<ReactivateModalProps> = ({
    show,
    onClose,
    onSuccess,
    email,
    getParamsEmail,
    inactiveAccount = false,
    mode,
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const { userData } = useSelector((state: any) => state?.bankidAuthFlow);

    function encrypt(plainText: string, passwordKey: string): string {
        const key = CryptoJS.enc.Utf8.parse(passwordKey);
        const iv = CryptoJS.enc.Hex.parse("00000000000000000000000000000000");
        const encrypted = CryptoJS.AES.encrypt(plainText, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });
        const base64Encrypted = encrypted.toString();
        return encodeURIComponent(base64Encrypted);
    }

    const handleModalClose = () => {
        onClose();
    };

    const handleSubmit = async () => {
        let URL: string | null = null;
        const passwordKey = "b14ca5898a4e4133bbce2ea2315a1916";

        try {
            let URL = "";
            let emailToUse = "";

            if (getParamsEmail && getParamsEmail !== "null") {
                // Email from URL parameters (email verification flows)
                emailToUse = encrypt(getParamsEmail, passwordKey);
            } else if (userData?.email) {
                // Email from QR code/BankID flow - use as-is
                console.log("ReactivateModal: Using QR code email directly:", userData.email);
                emailToUse = userData.email;
                console.log("ReactivateModal: userData email:", userData.email);
            } else if (email) {
                // Email from form data or regular login (needs encryption)
                console.log("ReactivateModal: Using form/regular email:", email);
                emailToUse = encrypt(email, passwordKey);
            }

            if (emailToUse) {
                URL = `/auth/reactivate-account-by-user?email=${emailToUse}&inactiveAccount=${inactiveAccount}`;
            }

            if (!URL) {
                console.error('ReactivateModal: No email source available');
                toast.error('Email address not found');
                return;
            }

            setIsLoading(true);

            const response = await axios.post(URL);

            if (response?.data?.statusCode === 200) {
                toast.success(t('Your account has been reactivated. You can login now.') as string);
                if (onSuccess) {
                    onSuccess();
                }
            } else if (response?.data?.statusCode === 404) {
                toast.error(response?.data?.message || 'Account not found');
            }

            handleModalClose();
            dispatch(resetBankIDFlow());
            dispatch(resetBankIDState());
        } catch (error: any) {
            console.error('Reactivation error:', error);
            toast.error('Failed to reactivate account. Please try again.');
            handleModalClose();
        } finally {
            setIsLoading(false);
        }
    };

    const getMessage = () => {
        if (inactiveAccount) {
            return t('Your account is inactive. Would you like to reactivate it now and sign in to start using Invozio?');
        }
        return t('Your account has been inactive for 180 days and has been deactivated. Would you like to reactivate it now and sign in to start using Invozio?');
    };

    return (
        <>
            {isLoading && <Loader />}
            <Modal
                centered
                show={show}
                onHide={handleModalClose}
                dialogClassName="modal-90w"
                aria-labelledby="reactivate-account-modal"
            >
                <Modal.Body>
                    <div
                        style={{
                            position: "absolute",
                            right: "7px",
                            fontSize: "22px",
                            cursor: "pointer",
                            top: 0,
                            color: "#000000ad",
                        }}
                    >
                        <RxCross2 onClick={handleModalClose} />
                    </div>

                    <RiErrorWarningLine
                        style={{
                            fontSize: "60px",
                            color: "#ff00008a",
                            display: "block",
                            margin: "0 auto",
                        }}
                    />

                    <p className="text-center mt-3">
                        {getMessage()}
                    </p>
                </Modal.Body>

                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        gap: "20px",
                        marginBottom: "18px",
                    }}
                >
                    <Button
                        style={{ width: 100 }}
                        variant="secondary"
                        onClick={handleModalClose}
                        disabled={isLoading}
                    >
                        {t("No")}
                    </Button>
                    <Button
                        style={{ width: 100, background: "#6750b8" }}
                        variant="primary"
                        onClick={handleSubmit}
                    >
                        {isLoading ? 'Loading...' : t("Yes")}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default ReactivateModal;
